
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors-theme focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "btn-theme-primary shadow-theme-subtle hover:shadow-theme-soft",
        destructive: "bg-theme-error text-theme-error-foreground hover:bg-theme-error-dark shadow-theme-subtle",
        outline: "border border-theme bg-theme-background hover:bg-theme-background-secondary hover:text-theme-foreground-secondary",
        secondary: "btn-theme-secondary shadow-theme-subtle hover:shadow-theme-soft",
        tertiary: "btn-theme-tertiary shadow-theme-subtle hover:shadow-theme-soft",
        ghost: "hover:bg-theme-background-secondary hover:text-theme-foreground-secondary",
        link: "text-theme-primary underline-offset-4 hover:underline hover:text-theme-primary-light",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
